/* ==========================================================================
   Component: Cart Drawer
   Owns drawer open/close, quantity steppers, cart updates and UI sync for
   the atomized cart drawer markup (snippets/cart-*.liquid).

   Dependencies (defined in theme.js, loaded first via defer order):
     theme.Drawers, theme.QtySelector, theme.cart, theme.Currency, theme.a11y

   Responsibilities are separated:
     - DrawerControls : open/close + focus trap
     - QtyBinding     : bind QtySelector to every stepper
     - CartSync       : POST quantity change, sync count/subtotal/rows and the
       shell-rendered empty state (.new-cart-drawer__empty)
   ========================================================================== */
(function() {
 'use strict';

 const selectors = {
  drawer: '#CartDrawer',
  form: '#CartDrawerForm',
  stepper: '.js-qty__wrapper',
  count: '[data-cart-count]',
  subtotal: '[data-cart-total]',
  body: '.new-cart-drawer__body',
  empty: '.new-cart-drawer__empty',
  row: '.new-cart-item',
  qtyNum: '.js-qty__num',
  lineDiscounts: '[data-line-discounts]',
  linePrice: '[data-line-price]',
  unitPrice: '[data-unit-price]'
 };

 // Subtotal/badge pending state: with a quantity-tiered discount the cart total
 // is NOT linear in quantity, so "old total + delta * unit" lands on a number
 // that matches no tier. While the server is being asked, the last real value
 // stays on screen wearing this class instead of a fabricated one.
 const PENDING_CLASS = 'is-cart-pending';

 const eventNames = {
  quantity: 'cart:quantity.cart-cart-drawer',
  updated: 'cart:updated',
  ajaxAdded: 'ajaxProduct:added',
  open: 'cart:open',
  close: 'cart:close'
 };

 const CartSync = {
  timers: {},
  // In-memory cart mirror (single optimistic state store — never attributes):
  //   lastQty[key]   — quantity as last requested (optimistic or server truth)
  //   subtotalCents  — subtotal in cents (optimistic or server truth)
  //   unitCents[key] — line unit price in cents (seeded from DOM, then server)
  lastQty: {},
  subtotalCents: null,
  unitCents: {},
  // Whether the last known server cart carried any line-level discount.
  // Seeded from the server-rendered markup, refreshed on every server response.
  hasLineDiscounts: false,
  // theme.Currency.formatMoney expects cents; parse "$159.00" → 15900.
  // Seeds unit/subtotal from server-rendered markup only; every server
  // response refreshes the mirror with exact values (final_line_price etc.).
  parsePrice: function(str) {
   const value = parseFloat(String(str).replace(/[^0-9.]/g, '')) || 0;
   return Math.round(value * 100);
  },

  // Optimistic + debounced quantity change: the stepper input is already
  // updated locally by QtySelector — mirror count/subtotal from the in-memory
  // cart state and coalesce rapid clicks into ONE /cart/change.js per line.
  changeQuantity: function(key, qty) {
   const prev = this.lastQty[key] != null ? this.lastQty[key] : 0;
   this.lastQty[key] = qty;
   const delta = qty - prev;
   if (delta === 0) return;

   const row = document.querySelector(selectors.row + '[data-item-id="' + key + '"]');
   if (qty <= 0 && row) row.remove();
   if (this.unitCents[key] == null) {
    // The visible price is a LINE total (snippets/cart-item-price.liquid), so
    // the unit price comes from its data attribute, not from the text.
    const priceEl = row && row.querySelector(selectors.unitPrice);
    if (priceEl) this.unitCents[key] = parseInt(priceEl.getAttribute('data-unit-price'), 10) || 0;
   }
   if (this.subtotalCents == null) {
    const subEl = document.querySelector(selectors.subtotal);
    this.subtotalCents = subEl ? this.parsePrice(subEl.textContent) : 0;
   }
   const unit = this.unitCents[key] || 0;

   document.querySelectorAll(selectors.count).forEach(function(el) {
    const cur = parseInt(el.innerText, 10) || 0;
    el.innerText = cur + delta;
   });

   // Item count is always linear in quantity, so it updates optimistically no
   // matter what. The money is not: a tiered discount can move the whole cart
   // to a different tier on this very click, which changes the price of lines
   // the shopper did not touch. Extrapolating would print a total that is
   // confidently wrong, so a discounted cart waits for the server instead.
   if (this.hasLineDiscounts) {
    this.setPending(true);
   } else if (unit) {
    this.subtotalCents += delta * unit;
    document.querySelectorAll(selectors.subtotal).forEach(function(el) {
     el.innerHTML = theme.Currency.formatMoney(this.subtotalCents, theme.settings.moneyFormat);
    }.bind(this));
    // No discount anywhere in the cart, so the line total is exactly qty × unit.
    if (row && qty > 0) this.renderLinePrice(row, qty * unit, qty * unit);
   }

   QtyBinding.updateMinusState();

   clearTimeout(this.timers[key]);
   this.timers[key] = setTimeout(function() {
    delete this.timers[key];
    this.postChange(key, qty);
   }.bind(this), 300);
  },
  postChange: function(key, qty) {
   theme.cart.changeItem(key, qty).then(function(cart) {
    const parsed = JSON.parse(cart);
    if (parsed.status === 422) {
     alert(parsed.message);
     CartSync.reconcileCart();
     return;
    }
    CartSync.applyServerState(parsed);
    document.dispatchEvent(new CustomEvent(eventNames.updated, {
     detail: { cart: parsed }
    }));
   }).catch(function() {
    CartSync.reconcileCart();
   });
  },

  // Roll the UI back to server truth after a failed change: drop pending
  // debounced posts (their target state is moot) and mirror /cart.js.
  reconcileCart: function() {
   Object.keys(this.timers).forEach(function(k) {
    clearTimeout(this.timers[k]);
   });
   this.timers = {};
   fetch('/cart.js', { credentials: 'same-origin' }).then(function(r) {
    return r.json();
   }).then(function(cart) {
    CartSync.applyServerState(cart);
   }).catch(function() {
    // Server truth is unreachable. Leaving the subtotal dimmed forever would
    // be its own lie, so drop the pending state and keep the last real values.
    CartSync.setPending(false);
   });
  },

  // Non-zero line-level discount allocations for one /cart.js line item.
  // Zero-value ones are dropped for the same reason the Liquid snippet drops
  // them: a "buy X get Y" style discount smears a -$0.00 allocation across
  // every paid line, which is noise.
  allocationsOf: function(item) {
   const raw = item.line_level_discount_allocations || item.discounts || [];
   return raw.filter(function(allocation) {
    return allocation && Number(allocation.amount) > 0;
   });
  },

  // Repaint one row's discount badges from server truth. This mirrors
  // snippets/cart-item-discounts.liquid — change one, change the other, or a
  // quantity edit will render differently from a page load.
  renderLineDiscounts: function(row, item) {
   const host = row.querySelector(selectors.lineDiscounts);
   if (!host) return;
   const allocations = this.allocationsOf(item);
   host.innerHTML = '';
   if (!allocations.length) return;
   const list = document.createElement('ul');
   list.className = 'new-cart-item__discount-list';
   list.setAttribute('role', 'list');
   allocations.forEach(function(allocation) {
    const li = document.createElement('li');
    li.className = 'new-cart-item__discount';
    const title = (allocation.discount_application && allocation.discount_application.title) || '';
    // Title goes in as text: it is merchant-authored and arrives as raw JSON.
    // The money string may legitimately carry markup from money_format, so it
    // gets its own span.
    li.appendChild(document.createTextNode(title + ' (-'));
    const money = document.createElement('span');
    money.innerHTML = theme.Currency.formatMoney(allocation.amount, theme.settings.moneyFormat);
    li.appendChild(money);
    li.appendChild(document.createTextNode(')'));
    list.appendChild(li);
   });
   host.appendChild(list);
  },

  // Repaint one row's line total. Mirrors snippets/cart-item-price.liquid:
  // final line total, plus the original struck through when it is higher.
  renderLinePrice: function(row, finalCents, originalCents) {
   const host = row.querySelector(selectors.linePrice);
   if (!host) return;
   const format = function(cents) {
    return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
   };
   let html = '<span class="new-cart-item__price-current">' + format(finalCents) + '</span>';
   if (finalCents < originalCents) {
    html += '<s class="new-cart-item__price-compare">' + format(originalCents) + '</s>';
   }
   host.innerHTML = html;
  },

  // Dim the subtotal, line totals and discount badges while the server catches
  // up, so a stale-but-real number is never mistaken for the new one.
  setPending: function(on) {
   document.querySelectorAll(selectors.subtotal + ',' + selectors.linePrice + ',' + selectors.lineDiscounts).forEach(function(el) {
    el.classList.toggle(PENDING_CLASS, on);
   });
  },

  // Seed hasLineDiscounts from server-rendered markup, so the very first
  // quantity click on an already-discounted cart skips extrapolation too.
  syncDiscountFlagFromDom: function() {
   this.hasLineDiscounts = !!document.querySelector(selectors.lineDiscounts + ' .new-cart-item__discount');
  },

  // True when the server cart holds a line that has no row in the drawer.
  // Removed lines (absent from the response) are the normal case and are not
  // counted; only a live line without a row means the keys were re-issued.
  lineKeysChanged: function(parsed) {
   const rows = document.querySelectorAll(selectors.form + ' ' + selectors.row);
   if (!rows.length) return false;
   const domKeys = {};
   rows.forEach(function(row) { domKeys[row.getAttribute('data-item-id')] = true; });
   return parsed.items.some(function(item) {
    return item.quantity > 0 && !domKeys[item.key];
   });
  },

  // Fetch the drawer's server-rendered rows through the Section Rendering API
  // and hand them to buildFromFragment, the same path the add-to-cart flow
  // uses. Only runs when line keys were re-issued, i.e. on a discount tier
  // change; ordinary quantity edits keep patching rows in place.
  rebuildFromServer: function(parsed) {
   const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
   const drop = {};
   Object.keys(this.lastQty).forEach(function(k) { drop[k] = true; });
   fetch(root + '?sections=cart-drawer', { credentials: 'same-origin' }).then(function(r) {
    if (!r.ok) throw new Error('sections ' + r.status);
    return r.json();
   }).then(function(sections) {
    // Forget the old keys; buildFromFragment re-seeds from the new rows.
    Object.keys(drop).forEach(function(k) {
     delete CartSync.lastQty[k];
     delete CartSync.unitCents[k];
    });
    // cart:updated is already dispatched by the caller (postChange) with the
    // same server cart, so it is not fired again here.
    CartSync.buildFromFragment(sections['cart-drawer']);
   }).catch(function() {
    // Without fresh rows the drawer would keep stale keys that the server
    // rejects on the next click. A reload is the only honest recovery.
    window.location.reload();
   });
  },

  // Mirror the server cart onto the DOM: quantities, row removal, header
  // count, subtotal and the empty state. Single entry point — every update
  // path (change response, reconcile, fragment fallback) lands here, and the
  // in-memory mirror is reset to server truth so optimistic drift can never
  // stick.
  applyServerState: function(parsed) {
   // Line keys are "variantId:hash" and the hash covers the line's discount
   // allocations. When a quantity change moves the cart to another discount
   // tier, EVERY line gets a new key — even lines whose quantity did not
   // change. Patching by key would then find no rows, and the "drop rows the
   // server no longer has" step below would empty the drawer while the cart
   // still holds items (seen on 2026-09-23 going from 2 to 3 pairs).
   // Line identity has changed, so re-render the rows from the server instead.
   if (this.lineKeysChanged(parsed)) {
    this.rebuildFromServer(parsed);
    return;
   }
   const present = {};
   parsed.items.forEach(function(item) {
    present[item.key] = true;
    this.lastQty[item.key] = item.quantity;
    if (item.quantity > 0) {
     // UNDISCOUNTED unit price. final_line_price / quantity would be an
     // average once a discount covers only part of a line, and that average is
     // the wrong basis for the optimistic path (which only ever runs on carts
     // with no discount at all). The rendered unit price is original too, so
     // the two stay in agreement.
     this.unitCents[item.key] = Math.round(item.original_line_price / item.quantity);
    }
    const row = document.querySelector(selectors.row + '[data-item-id="' + item.key + '"]');
    if (!row) return;
    if (item.quantity === 0) {
     row.remove();
    } else {
     const input = row.querySelector(selectors.qtyNum);
     if (input) { input.value = item.quantity; }
     this.renderLinePrice(row, item.final_line_price, item.original_line_price);
     this.renderLineDiscounts(row, item);
    }
   }.bind(this));
   Object.keys(this.lastQty).forEach(function(k) {
    if (!present[k]) { delete this.lastQty[k]; delete this.unitCents[k]; }
   }.bind(this));
   // A /cart/change.js response omits removed lines entirely — drop any DOM
   // row whose key is not present (deleted items), otherwise the row lingers
   // after count/subtotal have already synced.
   document.querySelectorAll(selectors.row).forEach(function(row) {
    const key = row.getAttribute('data-item-id');
    if (key && !present[key]) row.remove();
   });
   this.subtotalCents = parsed.total_price;
   this.hasLineDiscounts = parsed.items.some(function(item) {
    return CartSync.allocationsOf(item).length > 0;
   });

   document.querySelectorAll(selectors.count).forEach(function(el) {
    el.innerText = parsed.item_count;
   });
   document.querySelectorAll(selectors.subtotal).forEach(function(el) {
    el.innerHTML = theme.Currency.formatMoney(parsed.total_price, theme.settings.moneyFormat);
   });
   // Server truth has landed: whatever was dimmed is accurate again.
   this.setPending(false);

   const body = document.querySelector(selectors.body);
   const empty = document.querySelector(selectors.form + ' ' + selectors.empty);
   if (body) body.style.display = parsed.item_count > 0 ? '' : 'none';
   if (empty) empty.style.display = parsed.item_count === 0 ? '' : 'none';
    QtyBinding.updateMinusState();
    this.updateBlackFridayTier(parsed);
  },

  updateBlackFridayTier: function(parsed) {
    const banner = document.querySelector('[data-bf-cart-tier]');
    if (!banner) return;

    const eligibleHandles = [
      'aeroraise-3d-printed-sneakers',
      'aeriscape-3d-printed-sneakers',
      'leisure-path-3d-printed-casual-sneakers',
      'braidge-3d-printed-sneakers',
      'aerocloud-3d-printed-slides',
      'guardian-orbit-3d-printed-kids-shoes',
      'cloud-3d-printed-slippers',
      'gravitas-grid-derby-shoes'
    ];

    let shoeCount = 0;
    (parsed.items || []).forEach(function(item) {
      const urlHandle = (item.url && item.url.indexOf('/products/') !== -1) ? item.url.split('/products/')[1].split('?')[0] : '';
      const handle = item.handle || urlHandle;
      const titleLower = (item.product_title || item.title || '').toLowerCase();

      // Exclude accessories
      if (titleLower.includes('insole') || titleLower.includes('charm') || handle.includes('insole') || handle.includes('charm')) {
        return;
      }

      const isEligible = eligibleHandles.includes(handle);

      if (isEligible) {
        shoeCount += (item.quantity || 0);
      }
    });

    banner.setAttribute('data-shoe-count', String(shoeCount));
    const textEl = banner.querySelector('[data-bf-tier-text]');
    const barEl = banner.querySelector('[data-bf-tier-bar]');
    const trackEl = banner.querySelector('.bf-cart-tier__track');
    const steps = banner.querySelectorAll('.bf-cart-step');

    let pct = 0;
    let text = '';
    let isMax = false;

    if (shoeCount === 0) {
      pct = 0;
      text = 'Add 1 pair to unlock 15% off';
    } else if (shoeCount === 1) {
      pct = 50;
      text = 'Add 1 more pair to unlock 20% off End of Season styles';
    } else {
      pct = 100;
      isMax = true;
      text = '🎉 2+ pairs unlocked: 20% off End of Season styles';
    }

    if (textEl) textEl.textContent = text;
    if (barEl) barEl.style.width = pct + '%';
    if (trackEl) trackEl.setAttribute('aria-valuenow', String(pct));
    banner.setAttribute('data-progress', String(pct));
    if (isMax) {
      banner.classList.add('is-max');
    } else {
      banner.classList.remove('is-max');
    }

    if (steps.length >= 2) {
      steps[0].classList.toggle('is-active', shoeCount >= 1);
      steps[1].classList.toggle('is-active', shoeCount >= 2);
    }
  },

  // Fill the drawer from the inline add-response fragment
  // (sections['cart-drawer'] → .cart__items with data-count / data-cart-subtotal).
  // Contract check: PDP dispatchers may pass markup:null or a malformed
  // fragment — fall back to /cart.js instead of leaving stale page-load state.
  buildFromFragment: function(markup) {
   const doc = new DOMParser().parseFromString(markup, 'text/html');
   const fragment = doc.querySelector('.cart__items');
   const items = document.querySelector(selectors.form + ' .new-cart-drawer__items');
   if (!fragment || !items) {
    this.reconcileCart();
    return;
   }
   items.innerHTML = fragment.innerHTML;
   const count = parseInt(fragment.getAttribute('data-count'), 10);
   // Add-to-cart fragments always hold items; the history refresh below can
   // bring back an empty cart, so the empty state follows the count.
   const body = document.querySelector(selectors.body);
   if (body) body.style.display = count > 0 ? '' : 'none';
   const emptyNode = document.querySelector(selectors.form + ' ' + selectors.empty);
   if (emptyNode) emptyNode.style.display = count > 0 ? 'none' : '';
   document.querySelectorAll(selectors.count).forEach(function(el) {
    el.innerText = count;
   });
   const subtotal = fragment.getAttribute('data-cart-subtotal');
   if (subtotal) {
    this.subtotalCents = parseFloat(subtotal);
    document.querySelectorAll(selectors.subtotal).forEach(function(el) {
     el.innerHTML = theme.Currency.formatMoney(parseFloat(subtotal), theme.settings.moneyFormat);
    }.bind(this));
   }
   QtyBinding.bindAll(items);
   QtyBinding.updateMinusState();
   // The fragment is server-rendered, so its badges are truth — re-read the
   // flag and clear any dimming left over from an in-flight quantity edit.
   this.syncDiscountFlagFromDom();
   this.setPending(false);
   this.reconcileCart();
  }
 };

 const QtyBinding = {
  bindAll: function(root) {
   (root || document).querySelectorAll(selectors.stepper).forEach(function(el) {
    if (el.dataset.qtyBound === '1') return;
    el.dataset.qtyBound = '1';
    new theme.QtySelector(el, {
     namespace: '.cart-cart-drawer',
     isCart: true
    });
   });
  },
  // Minus stays clickable at any quantity: at 1 the next click decrements to
  // 0 and CartSync removes the line (changeItem(key, 0)).
  updateMinusState: function(root) {
   (root || document).querySelectorAll(selectors.stepper).forEach(function(el) {
    const num = el.querySelector(selectors.qtyNum);
    if (!num) return;
    const qty = parseInt(num.value, 10);
    if (num.dataset.id) CartSync.lastQty[num.dataset.id] = qty;
   });
  }
 };

 const DrawerControls = {
  open: function() {
   this.drawer.classList.add('drawer--is-open');
   document.documentElement.classList.add('js-drawer-open');
   document.documentElement.classList.add('lock-scroll');
   CartSync.reconcileCart();
   theme.a11y.trapFocus({
    container: this.form,
    elementToFocus: this.form,
    namespace: 'cartdrawer_focus'
   });
  },
  close: function() {
   this.drawer.classList.remove('drawer--is-open');
   document.documentElement.classList.remove('js-drawer-open');
   document.documentElement.classList.remove('lock-scroll');
   document.documentElement.classList.remove('js-drawer-closing');
   theme.a11y.removeTrapFocus({
    container: this.form,
    namespace: 'cartdrawer_focus'
   });
   if (document.activeElement && document.activeElement.blur) {
    document.activeElement.blur();
   }
  }
 };

 function CartDrawer() {
  this.form = document.querySelector(selectors.form);
  if (!this.form) {
   return;
  }
  this.drawer = document.getElementById('CartDrawer');
  if (!this.drawer) {
   return;
  }
  // Own the open/close wiring directly instead of theme.Drawers: the shared
  // Drawers instance keeps an internal isOpen that goes out of sync with the
  // actual DOM (drawer would never reopen after a first open/close cycle).
  document.querySelectorAll('.js-drawer-open-cart').forEach(function(btn) {
   btn.addEventListener('click', function(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    this.open();
   }.bind(this));
  }, this);
  document.addEventListener('click', function(evt) {
   const closeBtn = evt.target.closest('.js-drawer-close, .new-cart-drawer__close-btn');
   if (closeBtn && closeBtn.closest('#CartDrawer')) {
    evt.preventDefault();
    this.close();
    return;
   }
   if (!this.drawer.classList.contains('drawer--is-open')) return;
   // The qty/delete handlers remove the row synchronously (changeItem to 0);
   // the click then bubbles to this handler with a detached target whose
   // closest('#CartDrawer') is null — that must not count as an outside click
   // (it would close the drawer on every remove-at-0).
   if (!evt.target.isConnected) return;
   if (evt.target.closest('#CartDrawer')) return;
   if (evt.target.closest('.js-drawer-open-cart')) return;
   this.close();
  }.bind(this));
  document.addEventListener('keydown', (evt) => {
   if (evt.keyCode === 27) this.close();
  });
  this.init();
 }
 CartDrawer.prototype = Object.assign({}, CartDrawer.prototype, DrawerControls, {
  init: function() {
   QtyBinding.bindAll(this.form);
   QtyBinding.updateMinusState(this.form);
   // Page load already rendered the badges via Liquid; read them so the first
   // quantity click knows whether the cart total is tier-dependent.
   CartSync.syncDiscountFlagFromDom();
   document.addEventListener(eventNames.quantity, function(evt) {
    const detail = evt.detail;
    if (detail && detail[0] && detail[1] != null) {
     CartSync.changeQuantity(detail[0], detail[1]);
    }
   });
   // Delete: the button is a plain <a href="cart/change.js?...&quantity=0">
   // (legacy behavior) — intercept it so the item is removed in place and the
   // drawer never navigates to the /cart page.
   document.addEventListener('click', function(evt) {
    const del = evt.target.closest('.new-cart-item__delete');
    if (!del) return;
    evt.preventDefault();
    const key = del.getAttribute('data-item-key');
    if (key) CartSync.changeQuantity(key, 0);
   });

   document.addEventListener(eventNames.ajaxAdded, function() {
    QtyBinding.bindAll(this.form);
    this.open();
   }.bind(this));
   // Merge contract: the atomized PDP add flow dispatches cart:build with the
   // server fragment (sections['cart-drawer'] → .cart__items). Consume it so
   // the drawer is populated from the add response instead of showing the
   // page-load cart state.
   document.addEventListener('cart:build', function(evt) {
    if (evt && evt.detail && evt.detail.markup) {
     CartSync.buildFromFragment(evt.detail.markup);
    }
   });

   document.addEventListener(eventNames.open, this.open.bind(this));
   document.addEventListener(eventNames.close, this.close.bind(this));
  }
 });

 const isCartPage = document.body.classList.contains('template-cart');

 if (window.theme && theme.settings && theme.settings.cartType === 'drawer' && !isCartPage) {
  new CartDrawer();
 }

 // Back/forward cache: returning from checkout with the browser's Back button
 // can restore this page exactly as it was left, without asking the server.
 // A discount code applied or removed at checkout changes line prices and
 // badges, so the restored cart would show stale money. Re-render it from the
 // server whenever the page comes back from that cache.
 window.addEventListener('pageshow', function(evt) {
  if (!evt.persisted) return;
  if (isCartPage) {
   // theme.CartForm listens for this and re-fetches /cart?view=ajax.
   document.dispatchEvent(new CustomEvent('cart:build'));
   return;
  }
  if (!document.querySelector(selectors.form)) return;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  fetch(root + '?sections=cart-drawer', { credentials: 'same-origin' }).then(function(r) {
   if (!r.ok) throw new Error('sections ' + r.status);
   return r.json();
  }).then(function(sections) {
   CartSync.lastQty = {};
   CartSync.unitCents = {};
   CartSync.buildFromFragment(sections['cart-drawer']);
  }).catch(function() {});
 });
})();
